/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { zodResolver } from '@hookform/resolvers/zod';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import * as z from 'zod';

import { TestSuite } from '@/commons/protocols';
import DisplayError from '@/components/atoms/Error';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useGet, useMutate } from '@/net/commons';

const CreateSuite = () => {
    const { projectSlug } = useParams<{ projectSlug: string }>();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<any>(undefined);

    const navigate = useNavigate();

    const formSchema = z.object({
        name: z.string().nonempty('Name is required'),
        defaultEntryHref: z.union([z.string().url({ message: 'Invalid URL format' }).min(1), z.literal('')]),
    });

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: '',
            defaultEntryHref: '',
        },
    });

    const { handleSubmit } = form;

    const { trigger } = useMutate<TestSuite>(`/bff/project/${projectSlug}/suites`, {
        revalidate: true,
        method: 'POST',
    });

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        try {
            setIsLoading(true);
            const res = await trigger(values);
            navigate(`/story/${projectSlug}/${res?.slug}/live?onboarding`);
            setIsLoading(false);
        } catch (err) {
            setIsLoading(false);
            setError(err);
        }
    };

    const [errorMessage, setErrorMessage] = useState('');
    const [shouldCheckName, setShouldCheckName] = useState(false);
    const { getValues } = form;
    const { data: nameIsAvailable } = useGet<any>(
        shouldCheckName && getValues('name').length > 0
            ? `/bff/project/${projectSlug}/suites/${encodeURIComponent(getValues('name'))}/check`
            : null,
        {
            method: 'GET',
        },
    );

    useEffect(() => {
        if (shouldCheckName && nameIsAvailable && nameIsAvailable?.available === false) {
            setErrorMessage('This name already exists.');
        } else {
            setErrorMessage('');
        }
    }, [nameIsAvailable, shouldCheckName]);

    // debounce time to not dos the server
    const handleInputChange = useCallback(
        debounce(() => {
            if (getValues('name').length > 0) {
                setShouldCheckName(true);
            }
        }, 300), // 300ms debounce time
        [],
    );

    return (
        <div className="sm:max-w-3xl mx-auto">
            <h1 className="text-xl mb-6 ">Create a test suite</h1>
            {error && <DisplayError error={error} />}
            <Form {...form}>
                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-10">
                    <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row gap-2 items-center">Name</FormLabel>
                                <FormControl>
                                    <Input
                                        {...field}
                                        onInput={(e) => {
                                            setErrorMessage('');
                                            setShouldCheckName(false);
                                            field.onChange(e);
                                            handleInputChange();
                                        }}
                                    />
                                </FormControl>
                                <FormMessage>{errorMessage && errorMessage}</FormMessage>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="defaultEntryHref"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex flex-row gap-2 items-center">
                                    Default Entrypoint URL(optional)
                                </FormLabel>
                                <FormControl>
                                    <Input {...field} placeholder="https://" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className="flex sm:flex-row-reverse gap-2">
                        <Button
                            type="submit"
                            disabled={isLoading || (nameIsAvailable && nameIsAvailable?.available === false)}
                        >
                            Create suite
                        </Button>
                    </div>
                </form>
            </Form>
        </div>
    );
};

export default CreateSuite;
